/* eslint-disable react-refresh/only-export-components */
import {
  getMeetingLink,
  getMeetingProvider,
  getMeetingProviderTitle,
  withAuthUser,
  type MeetingProvider,
} from '@electron/shared/meetings.types';
import {
  useStores,
  JSONContentSchema,
  type JSONContent,
  EMPTY_JSON_CONTENT,
} from '@littlebird/sdk';
import { type MeetingCircle } from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { marked } from 'marked';
import { observer } from 'mobx-react-lite';
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'sonner';
import { match, P } from 'ts-pattern';
import { v4 as uuidv4 } from 'uuid';

import { Icons } from '@/assets/icons/icons';
import {
  AnimatedAudiowave,
  IconButton,
  Skeleton,
  Text,
  Time,
  TimeRange,
  Tooltip,
  XStack,
  YStack,
  Button,
} from '@/components/ui';
import { useAudioWaveSilenceDetection } from '@/components/ui/AnimatedAudiowave';
import { Avatar } from '@/components/ui/Avatar';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { getHtml, getText, isEmptyContent } from '@/components/ui/editor/utils';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';
import { classList } from '@/utils/cn';
import { formatDateWithDay, formatTimeRange } from '@/utils/date';
import { sanitizeHtml } from '@/utils/html';

import { electronApis } from '../electron-api';
import { EnsureMeetingMoveToProjectDropdownButton } from '../projects/move-to-project';

import { MEETING_PROVIDER_ICON } from './meeting-provider-icons';
import { getAttendeeDisplayName } from './meetings-transcript-display.hooks';
import {
  ensurePrepMeetingForCalendarEvent,
  meetingApis,
} from './meetings.query';
import { meetingStore } from './meetings.state';
import {
  type Meeting,
  type MeetingAudioEnergyLevel,
  type MeetingId,
  type CalendarEvent,
  type CalendarEventAttendee,
} from './meetings.types';
export {
  MeetingTranscript,
  transcriptFindTunnel,
} from './transcript-selection';

interface MeetingAudioWaveProps {
  orientation?: 'horizontal' | 'vertical';
  size?: 'default' | 'large';
  tooltip?: string;
  children?: ReactNode;
  isOpen?: boolean;
  className?: string;
  barWidth?: string;
  barGap?: string;
  barColor?: string;
  /** Subscribe to audio energy level updates. Returns an unsubscribe function. */
  subscribeEnergyLevel: (
    cb: (levels: MeetingAudioEnergyLevel) => void
  ) => () => void;
}

/** Audio wave component — store-agnostic, callers provide the energy level subscription. */
export function MeetingAudioWave({
  size = 'default',
  tooltip,
  children,
  isOpen,
  className,
  barColor,
  barWidth,
  barGap,
  subscribeEnergyLevel,
}: MeetingAudioWaveProps) {
  const { updateWaveRef, barColor: resolvedBarColor } =
    useAudioWaveSilenceDetection(subscribeEnergyLevel, barColor);

  const isLarge = size === 'large';
  const maxLength = isLarge ? 18 : 14;

  const Comp = (
    <button
      type="button"
      className={classList(
        'group/meeting-audio-wave',
        'rounded-xl smoothing-default',
        'text-foreground-muted',
        'hover:bg-surface-active hover:text-foreground',
        'flex gap-1 items-center',
        isLarge ? ['px-2', 'h-auto', 'min-h-8'] : ['pl-2.5 pr-1 py-0', 'h-8'],
        className
      )}
    >
      <AnimatedAudiowave
        updateWaveRef={updateWaveRef}
        maxLength={maxLength}
        gap={barGap ? barGap : isLarge ? '0.1rem' : undefined}
        barWidth={barWidth ? barWidth : isLarge ? '0.18rem' : undefined}
        barColor={resolvedBarColor}
      />
      {children}
    </button>
  );

  if (tooltip)
    return (
      <Tooltip sideOffset={isOpen ? 4 : 10} label={tooltip}>
        {Comp}
      </Tooltip>
    );

  return Comp;
}

const EMPTY_CONTENT = { html: '', text: '' };

function getNotesContent(notes: JSONContent | null | undefined) {
  const parsedNotes = JSONContentSchema.safeParse(notes);
  if (!parsedNotes.success || isEmptyContent(parsedNotes.data))
    return EMPTY_CONTENT;

  return {
    html: sanitizeHtml(getHtml(parsedNotes.data)),
    text: getText(parsedNotes.data),
  };
}

function getSummaryContent(summary: string | null | undefined) {
  if (!summary) return EMPTY_CONTENT;
  return {
    html: sanitizeHtml(marked.parse(summary, { async: false }) as string),
    text: summary,
  };
}

export const MeetingCopyButton = observer(function MeetingCopyButton({
  meetingId,
}: {
  meetingId: MeetingId;
}) {
  const { meetings } = useStores();
  const storeMeeting = meetings.get(meetingId);

  const notes = storeMeeting?.notes;
  const summary = storeMeeting?.summary?.trim();
  const transcript = meetingStore
    .getTranscriptLines(meetingId)
    .join('\n')
    .trim();

  const eventId = storeMeeting?.eventId;
  const isRecording = meetingStore.isMeetingIdRecording(meetingId);

  const handleCopy = useCallback(
    async (content: 'notes' | 'summary' | 'transcript') => {
      try {
        if (content === 'transcript') {
          if (!transcript) return;

          await navigator.clipboard.writeText(transcript);
        } else {
          const { html, text } =
            content === 'notes'
              ? getNotesContent(notes)
              : getSummaryContent(summary);

          if (!text.trim()) return;

          try {
            await navigator.clipboard.write([
              new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' }),
              }),
            ]);
          } catch {
            await navigator.clipboard.writeText(text);
          }

          if (content === 'summary')
            trackEvent('MEETING_SUMMARY_COPIED', {
              meeting_id: meetingId,
              tab: content,
              has_calendar_event: Boolean(eventId),
            });
        }

        toast.success(`Copied ${content}`);
      } catch {
        toast.error('Failed to copy to clipboard');
      }
    },
    [meetingId, eventId, notes, summary, transcript]
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <RoundIconButton size="sm" icon={Icons.copy} label="Copy" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" side="bottom" className="min-w-0 w-fit">
        <DropdownMenu.Item
          disabled={!summary || isRecording}
          onSelect={() => handleCopy('summary')}
        >
          <Icons.summarize className="size-3.5" />
          Copy summary
        </DropdownMenu.Item>
        <DropdownMenu.Item
          disabled={!transcript}
          onSelect={() => handleCopy('transcript')}
        >
          <Icons.transcripts className="size-3.5" />
          Copy transcript
        </DropdownMenu.Item>
        <DropdownMenu.Item
          disabled={isEmptyContent(storeMeeting?.notes ?? EMPTY_JSON_CONTENT)}
          onSelect={() => handleCopy('notes')}
        >
          <Icons.write className="size-3.5" />
          Copy note
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

export const UpcomingEventMoveToProjectButton = observer(
  function UpcomingEventMoveToProjectButton({
    event,
    existingMeeting,
    className,
  }: {
    event: CalendarEvent;
    existingMeeting?: Meeting;
    className?: string;
  }) {
    const { meetings, projects } = useStores();
    const [projectMenuOpen, setProjectMenuOpen] = useState(false);
    const meetingId =
      existingMeeting?.id ?? meetings.getMeetingByEventId(event.id)?.id;
    const currentProject = meetingId
      ? projects.getProjectForMeeting(meetingId)
      : null;

    return (
      <EnsureMeetingMoveToProjectDropdownButton
        meetingId={meetingId}
        ensureMeetingId={() =>
          ensurePrepMeetingForCalendarEvent(event, existingMeeting)
        }
        onOpenChange={setProjectMenuOpen}
        className={cn(
          className,
          !currentProject &&
            'opacity-0 pointer-events-none transition-opacity duration-150 touch:opacity-100 touch:pointer-events-auto group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-hover:pointer-events-auto',
          (currentProject || projectMenuOpen) &&
            'opacity-100 pointer-events-auto'
        )}
      />
    );
  }
);

export const UpcomingEventCard = memo(
  ({
    event,
    existingMeeting,
  }: {
    event: CalendarEvent;
    existingMeeting?: Meeting;
  }) => {
    const navigate = useNavigate();

    const startDateTime = new Date(event.start_datetime);
    const endDateTime = new Date(event.end_datetime);
    const month = format(startDateTime, 'MMM');
    const day = format(startDateTime, 'd');

    const now = new Date();
    const isHappeningNow = startDateTime <= now && endDateTime >= now;
    const isToday = now.toDateString() === startDateTime.toDateString();

    const handleCardClick = async () => {
      const meetingId = await ensurePrepMeetingForCalendarEvent(
        event,
        existingMeeting,
        {
          onError: () => navigate({ to: '/' }),
        }
      );
      navigate({ to: `/meetings/${meetingId}`, search: { tab: 'notes' } });
    };

    return (
      <XStack
        className={cn(
          'gap-4 group items-center',
          'px-2 py-2 rounded-2xl',
          'cursor-default',
          'hover:bg-surface-hover transition-colors'
        )}
        onClick={handleCardClick}
        role="button"
        aria-label={event.summary}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCardClick();
        }}
      >
        <XStack className="flex flex-col items-center justify-center size-12 rounded-xl text-foreground shrink-0 border-[0.5px] border-border-strong bg-surface-hover">
          <span className="text-[9px] uppercase leading-none mb-1 text-brand-red">
            {month}
          </span>
          <span className="text-lg leading-none font-serif">{day}</span>
        </XStack>

        <YStack className="flex-1 min-w-0 gap-0.5 no-record">
          <div className="inline-flex items-center gap-1 min-w-0 w-fit max-w-full">
            <Text className="font-medium text-base truncate first-letter:uppercase">
              {event.summary}
            </Text>
          </div>
          <Text
            className={cn(
              'text-xs',
              isHappeningNow ? 'text-primary' : 'text-foreground-muted'
            )}
          >
            {isHappeningNow ? (
              <>
                <span className="font-medium text-brand-emerald-green">
                  Now
                </span>
                <span className="mx-1 text-brand-emerald-green">·</span>
                <TimeRange
                  start={startDateTime}
                  end={endDateTime}
                  className="text-brand-emerald-green"
                />
              </>
            ) : (
              <>
                {isToday ? 'Today' : format(startDateTime, 'EEEE')}{' '}
                <Time date={startDateTime} />
              </>
            )}
          </Text>
        </YStack>

        <UpcomingEventMoveToProjectButton
          event={event}
          existingMeeting={existingMeeting}
          className="max-w-40 shrink-0 text-xs"
        />
      </XStack>
    );
  }
);

export const UpcomingEventCardSkeleton = memo(() => (
  <XStack
    aria-hidden
    className={cn(
      'gap-4 items-center',
      'px-2 py-2 rounded-2xl',
      'cursor-default'
    )}
  >
    <div
      aria-hidden
      className="size-12 rounded-xl shrink-0 bg-surface-active opacity-30"
    />
    <YStack className="flex-1 min-w-0 gap-1.5">
      <Skeleton className="h-4 w-2/3 max-w-[228px] rounded-md" />
      <Skeleton className="h-3 w-1/3 max-w-[88px] rounded-md" />
    </YStack>
  </XStack>
));
UpcomingEventCardSkeleton.displayName = 'UpcomingEventCardSkeleton';

const getProviderIcon = (provider: MeetingProvider | null | undefined) => {
  const iconName = provider ? MEETING_PROVIDER_ICON[provider] : undefined;
  return iconName ? Icons[iconName] : Icons.externalLink;
};

export const HEADER_CONTROL_SHAPE = 'h-10 rounded-xl';
export const HEADER_CONTROL_BOX = cn(
  HEADER_CONTROL_SHAPE,
  'border border-border-strong bg-muted'
);

interface MeetingDateDropdownProps {
  meeting: Meeting;
}

export const MeetingDateDropdown = observer(
  ({ meeting }: MeetingDateDropdownProps) => {
    void settingsStore.timeFormat;

    const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();

    const event = meeting.event_id
      ? calendarEvents.find((e) => e.id === meeting.event_id)
      : null;

    const displayDate = format(
      new Date(
        meeting.first_transcript_at ??
          event?.start_datetime ??
          meeting.event_start_at ??
          meeting.created_at
      ),
      'MMM d'
    );

    const handleOpenCalendar = () => {
      if (event?.htmlLink) {
        electronApis.system.openExternal(
          withAuthUser(event.htmlLink, event) ?? event.htmlLink
        );
      }
    };

    if (!event) {
      return (
        <span
          className={cn(
            HEADER_CONTROL_BOX,
            'flex items-center gap-1.5 px-3 text-sm text-foreground-muted whitespace-nowrap'
          )}
        >
          <Icons.calendar className="!size-4" strokeWidth={1.6} />
          {displayDate}
        </span>
      );
    }

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className={cn(HEADER_CONTROL_BOX, 'gap-1.5 px-3')}
          >
            <Icons.calendar
              className="!size-4 text-foreground-muted"
              strokeWidth={1.6}
            />
            <span className="text-sm font-medium text-foreground">
              {displayDate}
            </span>
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" sideOffset={4} className="w-64">
          <div className="p-2 no-record">
            <div className="flex items-start gap-2 mb-1">
              <div className="size-2 rounded-full bg-teal-500 mt-1.5 shrink-0" />
              <span className="text-sm font-medium text-foreground">
                {event.summary}
              </span>
            </div>
            <div className="text-xs text-foreground-muted ml-4 mb-3">
              {formatDateWithDay(event.start_datetime)} ·{' '}
              {formatTimeRange(event.start_datetime, event.end_datetime)}
            </div>
            {event.htmlLink && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full gap-2"
                onClick={handleOpenCalendar}
              >
                <Icons.googleCalendar className="size-4" />
                Open in Google Calendar
              </Button>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

const JOIN_HIGHLIGHT_LEAD_TIME_MS = 5 * 60 * 1000;
const JOIN_HIGHLIGHT_GRACE_TIME_MS = 5 * 60 * 1000;
const JOIN_HIGHLIGHT_TICK_MS = 30 * 1000;

interface MeetingDetailJoinButtonProps {
  meeting: Meeting;
}

export const MeetingDetailJoinButton = observer(
  ({ meeting }: MeetingDetailJoinButtonProps) => {
    const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();

    const event = meeting.event_id
      ? calendarEvents.find((e) => e.id === meeting.event_id)
      : null;

    const eventLink = event
      ? withAuthUser(getMeetingLink(event), event)
      : undefined;
    const provider = getMeetingProvider(event);
    const providerTitle = provider ? getMeetingProviderTitle(provider) : null;
    const isEventEnded = event
      ? new Date(event.end_datetime) < new Date()
      : false;
    const showJoinTrigger = eventLink && !isEventEnded;

    const [nowMs, setNowMs] = useState(Date.now);

    const startMs = event ? new Date(event.start_datetime).getTime() : NaN;
    const isStartingSoon =
      Number.isFinite(startMs) &&
      startMs - nowMs <= JOIN_HIGHLIGHT_LEAD_TIME_MS &&
      nowMs - startMs <= JOIN_HIGHLIGHT_GRACE_TIME_MS;
    const highlightWindowPassed =
      Number.isFinite(startMs) &&
      nowMs - startMs > JOIN_HIGHLIGHT_GRACE_TIME_MS;

    useEffect(() => {
      if (!event || isEventEnded || highlightWindowPassed) return;
      const intervalId = window.setInterval(() => {
        setNowMs(Date.now());
      }, JOIN_HIGHLIGHT_TICK_MS);
      return () => window.clearInterval(intervalId);
    }, [event, isEventEnded, highlightWindowPassed]);

    const { isBusyWithThis, isBusyWithAnother } = match(meetingStore.recording)
      .with(
        { status: P.union('recording', 'stopping', 'loading') },
        ({ meetingId }) => {
          const isBusyWithThis = meetingId === meeting.id;
          return {
            isBusyWithThis,
            isBusyWithAnother: !isBusyWithThis,
          };
        }
      )
      .with({ status: 'idle' }, () => ({
        isBusyWithThis: false,
        isBusyWithAnother: false,
      }))
      .exhaustive();

    const handleJoinMeeting = async () => {
      if (!event) return;

      if (eventLink) {
        electronApis.system.openExternal(eventLink, { preferMeetingApp: true });
      }
      void electronApis.meetings.markUpcomingMeetingJoined(event.id);

      if (isBusyWithThis) return;

      if (isBusyWithAnother) {
        console.error(
          'The button should be disabled if another meeting is active.'
        );
      } else {
        try {
          const isPreparedMeetingFirstRecording = meetingStore.isPrepState(
            meeting.id
          );
          await meetingApis.enqueueStartMeeting({
            payload: {
              meetingId: meeting.id,
              startReason: 'resume',
              resumeSource: isPreparedMeetingFirstRecording
                ? undefined
                : 'meeting_page',
            },
          });
        } catch (_error) {
          electronApis.lbNotifications.sendNotification({
            id: uuidv4(),
            view: 'basic',
            sound: 'problem',
            payload: {
              title: 'Recording Failed',
              message: `Couldn't start recording for "${event.summary}"`,
              route: {
                to: '/meetings/$meetingId',
                params: { meetingId: meeting.id },
              },
            },
          });
        }
      }
    };

    if (!showJoinTrigger) return null;

    const ProviderIcon = getProviderIcon(provider);

    return (
      <Button
        size="sm"
        variant={isStartingSoon ? 'secondary' : 'outline'}
        className="h-10 gap-1.5 rounded-xl shrink-0"
        disabled={isBusyWithAnother}
        tooltip={
          isBusyWithAnother ? 'Another meeting is being recorded' : undefined
        }
        onClick={handleJoinMeeting}
      >
        <ProviderIcon className="size-3.5" />
        Join{providerTitle && ` ${providerTitle}`}
      </Button>
    );
  }
);

interface AttendeesDropdownProps {
  attendees: CalendarEventAttendee[];
}

const AvatarStatusBadge = ({
  responseStatus,
  optional,
}: {
  responseStatus?: string | null;
  optional?: boolean | null;
}) => {
  if (optional) {
    return (
      <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-surface border border-surface-hover flex items-center justify-center text-[7px] font-bold text-foreground-muted">
        ?
      </span>
    );
  }

  if (responseStatus === 'accepted') {
    return (
      <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-green-600 border border-surface flex items-center justify-center font-bold text-white">
        <Icons.check
          className="scale-[.8] translate-y-[0.03rem]"
          strokeWidth={2.5}
        />
      </span>
    );
  }

  if (responseStatus === 'declined') {
    return (
      <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-red-500 border border-surface flex items-center justify-center font-bold text-white">
        <Icons.cancel className="scale-[.8]" />
      </span>
    );
  }

  return null;
};

export const AttendeeAvatar = ({
  name,
  email,
  avatarUrl,
  className,
  responseStatus,
  optional,
}: {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  className?: string;
  responseStatus?: string | null;
  optional?: boolean | null;
}) => {
  const initial = (name || email || '?').charAt(0).toUpperCase();

  return (
    <div
      className={cn(
        'bg-secondary-active size-6 rounded-full flex items-center justify-center flex-shrink-0 relative',
        className
      )}
    >
      <Avatar
        avatarUrl={avatarUrl}
        alt={name || email || 'Attendee'}
        fallback={
          <span className="text-foreground-muted text-xs font-bold">
            {initial}
          </span>
        }
      />
      <AvatarStatusBadge responseStatus={responseStatus} optional={optional} />
    </div>
  );
};

const CIRCLE_FACES_SIZES = {
  sm: {
    box: 'size-6',
    face: 'size-4 [&_span]:text-[0.5rem]',
    single: 'size-6',
  },
  lg: { box: 'size-10', face: 'size-7', single: 'size-10 [&_span]:text-sm' },
};

export const CircleFaces = ({
  circle,
  size,
}: {
  circle: Pick<MeetingCircle, 'name' | 'emails' | 'faces'>;
  size: keyof typeof CIRCLE_FACES_SIZES;
}) => {
  const sizes = CIRCLE_FACES_SIZES[size];
  const [first, second] = circle.faces;

  if (!second) {
    return (
      <AttendeeAvatar
        name={first?.name ?? circle.name}
        email={first?.email ?? circle.emails[0]}
        avatarUrl={first?.avatarUrl}
        className={sizes.single}
      />
    );
  }

  return (
    <span className={cn('relative shrink-0', sizes.box)}>
      {[first, second].map((face, index) => (
        <AttendeeAvatar
          key={face.email}
          name={face.name}
          email={face.email}
          avatarUrl={face.avatarUrl}
          className={cn(
            'absolute ring-2 ring-surface',
            sizes.face,
            index === 0 ? 'left-0 top-0' : 'bottom-0 right-0'
          )}
        />
      ))}
    </span>
  );
};

export const AttendeesDropdown = ({ attendees }: AttendeesDropdownProps) => {
  // Grouping logic
  const groupedAttendees = useMemo(() => {
    const groups: Record<string, CalendarEventAttendee[]> = {};
    attendees.forEach((attendee) => {
      const email = attendee.email;
      const domain = email?.split('@')[1] || 'Unknown';
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(attendee);
    });
    return groups;
  }, [attendees]);

  const sortedDomains = Object.keys(groupedAttendees).sort();

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard');
  };

  if (attendees.length === 0) return null;

  const displayAttendees = attendees.slice(0, 2);
  const remainingCount = attendees.length - displayAttendees.length;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            HEADER_CONTROL_BOX,
            'text-foreground-subtle hover:text-foreground-subtle px-3'
          )}
        >
          <div className="flex items-center gap-1">
            <div className="flex items-center -space-x-2">
              {displayAttendees.map((attendee) => (
                <AttendeeAvatar
                  key={attendee.email}
                  name={attendee.displayName}
                  email={attendee.email}
                  avatarUrl={attendee.avatarUrl}
                  className="border border-surface size-6"
                />
              ))}
            </div>
            {remainingCount > 0 && (
              <span className="text-sm text-foreground-muted">
                +{remainingCount}
              </span>
            )}
          </div>
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="start" sideOffset={4}>
        <DropdownMenu.Label>Attendees</DropdownMenu.Label>
        <DropdownMenu.Separator className="my-0" />
        <ScrollMask className="max-h-[400px] w-full overflow-x-hidden">
          {sortedDomains.map((domain) => (
            <div key={domain} className="mb-2 no-record">
              <div className="px-3 pt-3 pb-1.5 text-xs font-medium text-foreground-muted">
                {domain}
              </div>
              <div className="flex flex-col gap-0.5">
                {groupedAttendees[domain].map((attendee) => (
                  <div
                    key={attendee.email}
                    className="flex items-center justify-between px-2.5 py-1.5 hover:bg-surface-hover rounded-md group transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AttendeeAvatar
                        name={attendee.displayName}
                        email={attendee.email}
                        avatarUrl={attendee.avatarUrl}
                        responseStatus={attendee.responseStatus}
                        optional={attendee.optional}
                      />
                      <YStack className="min-w-0">
                        <span className="text-sm truncate text-foreground-subtle">
                          {getAttendeeDisplayName(attendee) ?? attendee.email}
                        </span>
                        {attendee.email && (
                          <span className="text-xs truncate text-foreground-muted">
                            {attendee.email}
                          </span>
                        )}
                      </YStack>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 touch:opacity-100 group-hover:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity">
                      {attendee.email && (
                        <IconButton
                          icon={Icons.copy}
                          size="xs"
                          variant="ghost"
                          tooltip="Copy email"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyEmail(attendee.email!);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {attendees.length === 0 && (
            <div className="p-4 text-center text-sm text-foreground-muted">
              No attendees
            </div>
          )}
        </ScrollMask>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
